import { useState } from "react";
import { Plus, Check } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "../../components/shared/Toast";
import { useAppData } from "../../lib/AppDataContext";
import { STATUSES, STATUS_META } from "../../lib/leads";

/* Admin-only screen for managing lead statuses. The default 10 are
   read-only; custom statuses added here flow into every dropdown,
   badge, and kanban column automatically because registerLeadStatus
   mutates the shared registry. Consumers re-render via the
   statusVersion bump from AppDataContext. */
export function LeadSettings() {
  const { statusVersion, refreshLeadStatuses, leadStatusesApi } = useAppData();
  void statusVersion; // re-read STATUSES / STATUS_META on each bump
  const [label, setLabel] = useState("");
  const [err, setErr] = useState("");

  const [busy, setBusy] = useState(false);

  /* Persists to the API, then re-reads the list so every screen picks up the
     new status. The old version mutated an in-memory array — which is exactly
     why a new status disappeared on refresh. */
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!label.trim()) { setErr("Status name is required"); return; }
    setBusy(true);
    try {
      await leadStatusesApi.create(label.trim());
      await refreshLeadStatuses();
      setLabel("");
      toast("Status added");
    } catch (e2) {
      setErr(e2?.message || "Could not add status");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (code) => {
    if (!confirm(`Remove the "${STATUS_META[code]?.label || code}" status?`)) return;
    try {
      await leadStatusesApi.remove(code);
      await refreshLeadStatuses();
      toast("Status removed");
    } catch (e2) {
      toast(e2?.message || "Could not remove status", "error");
    }
  };

  return (
    <div>
      <PageHeader accent="sales" title="Lead Settings" subtitle="Manage lead statuses available across all teams" />

      <Card className="p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Plus className="w-4 h-4 text-blue-600" />Add a new status
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          New statuses appear immediately in the status dropdown, the filter, and the kanban board.
        </p>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status name</label>
            <input
              value={label}
              onChange={(e) => { setLabel(e.target.value); setErr(""); }}
              placeholder='e.g. "Qualified", "Site Tour Scheduled"'
              className={`w-full text-sm rounded-lg ring-1 ring-inset px-3 py-2 focus:outline-none focus:ring-blue-500 ${err ? "ring-red-400" : "ring-slate-300"}`}
            />
            {err && <p className="text-xs text-red-600 mt-1.5">{err}</p>}
          </div>
          <Btn type="submit" variant="primary" disabled={busy}><Check className="w-4 h-4" />{busy ? "Adding\u2026" : "Add status"}</Btn>
        </form>
      </Card>

      <Card>
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">All statuses</h3>
          <p className="text-xs text-slate-500 mt-0.5">{STATUSES.length} statuses &middot; built-in ones can't be removed</p>
        </div>
        <div className="overflow-x-auto -mx-3 sm:mx-0"><table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-2.5">Label</th>
              <th className="px-4 py-2.5">Code</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {STATUSES.map((s) => {
              const meta = STATUS_META[s];
              return (
                <tr key={s} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5"><StatusBadge status={s} /></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{s}</td>
                  <td className="px-4 py-2.5">
                    {meta?.custom
                      ? <Badge cls="bg-blue-50 text-blue-700 ring-blue-600/20">Custom</Badge>
                      : <Badge cls="bg-slate-100 text-slate-600 ring-slate-400/20">Built-in</Badge>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {meta?.custom && (
                      <button
                        onClick={() => remove(s)}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}
